import { Link,  useNavigate,  useSearchParams } from 'react-router-dom' 
import { 
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator,
} from '@/components/ui/breadcrumb' 
import { MarketResearchByIdKeys, useMarketResearchByIdQuery } from '@/queries/useMarketResearchByIdQuery'
import TopMessage from '@/components/TopMessage'
import { AnimatePresence } from 'framer-motion' 
import { useEffect,  useState } from 'react'  
import { TypographyH1, TypographyMuted, TypographySmall, TypographyXS } from "@/components/ui/typography" 
import { MarketResearch } from "@/types/MarketResearch"
import { formatDateToString } from "@/utils/date"
import { Calendar1, CalendarDays, Camera, CameraOff,  CircleArrowLeft,  Cog,  Dot, Pencil, SquareX,  Store, Trash2 } from "lucide-react"
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion" 
import useDisclosure from '@/hooks/useDisclosure'
import AddProductInMarketResearchDialog from './components/AddProductInMarketResearchDialog' 
import EditMarketResearchDialog from './components/EditMarketResearchDialog'
import DeleteMarketResearchDialog from './components/DeleteMarketResearchDialog'
import TableProductsMarketResearch from './components/TableProducts'
import TablePDVMarketResearch from './components/TablePDV'
import ButtonWithTooltip from '@/components/ButtonWithTooltip'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { changeStatusMarketResearch } from '@/api/business/marketResearch'
import { useToast } from '@/components/ui/use-toast'
import { isAxiosError } from '@/api/business'
import { MarketResearchKeys } from '@/queries/useMarketResearchQuery'
import Button from '@/components/Button'
import LoadingScreen from '@/pages/LoadingScreen'
 

const MarketResearchSettings = () => { 
    const navigate = useNavigate() 
    const [searchParams] = useSearchParams(); 
    const queryClient = useQueryClient()
    
    const { toast } = useToast()

    const id = searchParams.get("search"); 
    if(!id) {
        navigate('/market-research')
    }
    const [dataMarketResearch, setDataMarketResearch] = useState<MarketResearch>()


    const {
        isOpen: isAddProductInMarketResearchDialog,
        onClose: onCloseAddProductInMarketResearchDialog,
        onOpen: onAddProductInMarketResearchDialog,
    } = useDisclosure()

    const {
        isOpen: isEditMarketResearchDialog,
        onClose: onCloseEditMarketResearchDialog,
        onOpen: onOpenEditMarketResearchDialog,
    } = useDisclosure()

    const {
        isOpen: isDeleteMarketResearchDialog,
        onClose: onCloseDeleteMarketResearchDialog,
        onOpen: onOpenDeleteMarketResearchDialog,
    } = useDisclosure()

    const { data, isLoading } = useMarketResearchByIdQuery(Number(id)) 

    const { mutate, isPending } = useMutation({
        mutationFn: changeStatusMarketResearch,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: MarketResearchByIdKeys.details() }) 
            queryClient.invalidateQueries({ queryKey: MarketResearchKeys.details() })
            queryClient.invalidateQueries({ queryKey: MarketResearchKeys.lists() }) 
            toast({
                title: 'Mudança do status da pesquisa efetuado com sucesso',
            })
        },
        onError: (err) => {
            let errorMessage =
                'Não foi possível efetuar a mudança do status. Tente novamente mais tarde.'

            if (isAxiosError(err)) {
                errorMessage = err.response?.data.message || errorMessage
            }

            toast({
                title: 'Erro ao excluir efetuar a mudança do status',
                description: errorMessage,
                variant: 'destructive',
            })
        },
    })

    useEffect(() => {
        data && setDataMarketResearch(data[0])
    }, [data]); 


    if (isLoading) return <LoadingScreen />

    return (
        <div className="flex flex-col h-full overflow-auto p-4 bg-gray-50"> 
            <Breadcrumb>
                <BreadcrumbList>
                    <BreadcrumbItem>
                        <BreadcrumbLink asChild>
                            <Link
                                className="hover:underline"
                                to="/market-research"
                            >
                                Planejamento de Pesquisa
                            </Link>
                        </BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator /> 
                    <BreadcrumbItem>
                        <BreadcrumbLink> 
                                Configurações 
                        </BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator /> 
                    <BreadcrumbItem>
                        <BreadcrumbPage>
                            #{id} {dataMarketResearch && dataMarketResearch.NM_PESQUISA}
                        </BreadcrumbPage>
                    </BreadcrumbItem>
                </BreadcrumbList>
            </Breadcrumb>
            <AnimatePresence>
                {isLoading && (
                    <TopMessage
                        text="Carregando dados iniciais..."
                        variant="loading"
                        className="text-green-500 bg-green-100"
                    />
                )} 
                {isPending && (
                    <TopMessage
                        text="Efetuando mudanças..."
                        variant="loading"
                        className="text-green-500 bg-green-100"
                    />
                )} 
            </AnimatePresence>
           
            {dataMarketResearch && (
                <div className="flex flex-col h-full py-4 gap-4">
                    <div className="flex flex-col gap-4 justify-between mb-4 sm:items-center sm:flex-row">
                        <div className="flex gap-3 items-center">
                            <div className="flex justify-center items-center w-10 h-10 rounded-lg bg-primary/10">
                                <Cog className="w-5 h-5 text-primary" />
                            </div>
                            <div>
                                <div className="flex items-center gap-2">
                                    <TypographyH1 className="text-2xl font-semibold">
                                        {dataMarketResearch.NM_PESQUISA}
                                    </TypographyH1>
                                    <ButtonWithTooltip
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0"
                                        tooltipContent={<p>Editar pesquisa</p>}
                                        onClick={onOpenEditMarketResearchDialog}
                                    >
                                        <Pencil className="h-4 w-4" />
                                    </ButtonWithTooltip>
                                    <ButtonWithTooltip
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                                        tooltipContent={<p>Excluir pesquisa</p>}
                                        onClick={onOpenDeleteMarketResearchDialog}
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </ButtonWithTooltip>
                                </div>
                                <TypographyMuted>
                                    Verifique as configurações padrões da pesquisa
                                </TypographyMuted>
                            </div>
                        </div>
                         <Button className="md:flex hidden flex-col items-center text-xs" onClick={() => navigate(-1)}>
                            <CircleArrowLeft size={18} />
                            <TypographyMuted className="text-white">Voltar</TypographyMuted> 
                        </Button>
                    </div> 

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"> 
                        <div className="flex items-start gap-2">
                            <Store size={14} className="mt-1 text-gray-500 shrink-0" />
                            <div>
                                <p className="text-xs text-gray-600">Revenda</p>
                                <p className="text-sm font-medium text-gray-900">
                                    {dataMarketResearch.DS_REVENDA} ({dataMarketResearch.SK_REVENDA})
                                </p>
                            </div>
                        </div>
 
                        <div className="flex items-start gap-2">
                            <CalendarDays size={14} className="mt-1 text-gray-500 shrink-0" />
                            <div>
                                <p className="text-xs text-gray-600">Período</p>
                                <p className="text-sm font-medium text-gray-900">
                                    {formatDateToString(dataMarketResearch.SK_TEMPO_INICIO)} -{" "}
                                    {formatDateToString(dataMarketResearch.SK_TEMPO_FIM)}
                                </p>
                            </div>
                        </div>
 
                        <div className="flex items-start gap-2">
                            <CalendarDays size={14} className="mt-1 text-gray-500 shrink-0" />
                            <div>
                                <p className="text-xs text-gray-600">Tipo Ciclo</p>
                                <p className="text-sm font-medium text-gray-900">
                                    {dataMarketResearch.DD_TIPO_CICLO}
                                </p>
                            </div>
                        </div>

                        <div className="flex items-start gap-2">
                            {dataMarketResearch.DD_COLETA_IMAGEM 
                                ? (<Camera size={18} className="text-gray-500"/>)
                                : (<CameraOff size={18} className="text-gray-500"/>)
                            }
                            <div className="grid">
                                <TypographySmall className="text-xs text-gray-600">Obrigatóriedade para imagem</TypographySmall>
                                <TypographyXS className="text-sm font-medium text-gray-900">
                                    {dataMarketResearch.DD_COLETA_IMAGEM 
                                        ? 'Sim'
                                        : 'Não'
                                    }
                                </TypographyXS>
                            </div>
                        </div>
                        
                        <div className="flex items-start gap-2">
                            <SquareX size={18} className="text-gray-500"/>
                            <div className="grid">
                                <TypographySmall className="text-xs text-gray-600">Tipo Encerramento</TypographySmall>
                                <TypographyXS className="text-sm font-medium text-gray-900">{dataMarketResearch.DD_TIPO_ENCERRAMENTO}</TypographyXS>
                            </div>
                        </div>
                        <div className="flex items-start gap-2">
                            <Calendar1 size={18} className="text-gray-500"/>
                            <div className="grid">
                                <TypographySmall className="text-xs text-gray-600">Dias de carência</TypographySmall>
                                <TypographyXS className="text-sm font-medium text-gray-900">{dataMarketResearch.QTD_DIAS_CARENCIA}</TypographyXS>
                            </div>
                        </div>

                        <div className="flex items-start gap-2">
                            <Dot className="text-gray-500"/>
                            <div className="grid">
                                <TypographySmall className="text-xs text-gray-600">Status atual</TypographySmall>
                                <TypographyXS className="text-sm font-medium text-gray-900">{dataMarketResearch.DD_STATUS}</TypographyXS>
                                {
                                    dataMarketResearch.DD_STATUS == 'Rascunho'
                                    && (
                                        <ButtonWithTooltip
                                            className="flex items-center justify-center w-auto gap-2 px-4 rounded-md mt-4"
                                            tooltipContent={<p>Publicar pesquisa</p>}   
                                            onClick={() => mutate({SK_PESQUISA: dataMarketResearch.SK_PESQUISA, DD_STATUS: 'Publicado'})}
                                            size="sm"
                                        > 
                                            Publicar pesquisa
                                        </ButtonWithTooltip>
                                    ) 
                                }
                                {
                                    dataMarketResearch.DD_STATUS == 'Publicado'
                                    && (
                                        <ButtonWithTooltip
                                            className="flex items-center justify-center w-auto gap-2 px-4 rounded-md mt-4"
                                            tooltipContent={<p>Encerrar pesquisa</p>}   
                                            onClick={() => mutate({SK_PESQUISA: dataMarketResearch.SK_PESQUISA, DD_STATUS: 'Encerrado'})}
                                            size="sm"
                                        > 
                                            Encerrar pesquisa
                                        </ButtonWithTooltip>
                                    ) 
                                }
                            </div>
                        </div>

                    </div> 

                    <Accordion
                        type="single"
                        collapsible
                        className="w-full" 
                        >
                        <AccordionItem value="item-1">
                            <AccordionTrigger>Produtos da pesquisa</AccordionTrigger>
                            <AccordionContent className="flex flex-col gap-4 text-balance max-h-96"> 
                                <TableProductsMarketResearch id={dataMarketResearch.SK_PESQUISA} onAddProductInMarketResearchDialog={onAddProductInMarketResearchDialog}/>
                            </AccordionContent>
                        </AccordionItem>
                        <AccordionItem value="item-2">
                            <AccordionTrigger>Clientes da pesquisa</AccordionTrigger>
                            <AccordionContent className="flex flex-col gap-4 text-balance max-h-96"> 
                                <TablePDVMarketResearch id={dataMarketResearch.SK_PESQUISA}/>
                            </AccordionContent>
                        </AccordionItem> 
                    </Accordion>

                    <AddProductInMarketResearchDialog 
                        isOpen={isAddProductInMarketResearchDialog} 
                        onClose={onCloseAddProductInMarketResearchDialog} 
                        ids={dataMarketResearch && dataMarketResearch.SK_PESQUISA}
                    />

                    <EditMarketResearchDialog
                        isOpen={isEditMarketResearchDialog}
                        onClose={onCloseEditMarketResearchDialog}
                        data={dataMarketResearch}
                    />

                    <DeleteMarketResearchDialog
                        isOpen={isDeleteMarketResearchDialog}
                        onClose={onCloseDeleteMarketResearchDialog}
                        skPesquisa={dataMarketResearch.SK_PESQUISA}
                        nmPesquisa={dataMarketResearch.NM_PESQUISA}
                    />
                </div>
            )}
        </div>
    )
}

export default MarketResearchSettings
